import React, {useEffect, useMemo, useState} from 'react'
import {observer} from 'mobx-react-lite'
import {Accordion, AccordionTab} from 'primereact/accordion'
import Btn from '../../ui/Btn'
import Input from '../../ui/Input'
import appStore from '../../storage/AppStore'
import clubStore from '../../storage/ClubStore'
import systemStore from '../../storage/SystemStore'
import '../../common/game.css'
import Select from "../../ui/Select";
import {Slider} from "primereact/slider";

const SEX_OPTIONS = [
  {id: 'M', name: 'male'},
  {id: 'F', name: 'female'},
  {id: 'MIXED', name: 'mixed'},
]

const EMPTY_DIVISION_FORM = {
  id: 0,
  name: '',
  age_from: 12,
  age_to: 13,
  sex: 'MIXED',
  sort_order: 1,
}

const EMPTY_GROUP_FORM = {
  id: 0,
  division_id: 0,
  name: '',
  sort_order: 0,
}

const numOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const toInt = (v, fallback = 0) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

const CompetitionDivisions = ({tournamentId}) => {
  const [activeIndex, setActiveIndex] = useState(0)

  const [showDivisionForm, setShowDivisionForm] = useState(false)
  const [divisionForm, setDivisionForm] = useState({...EMPTY_DIVISION_FORM})

  const [showGroupFormByDivision, setShowGroupFormByDivision] = useState({})
  const [groupFormByDivision, setGroupFormByDivision] = useState({})

  const [savingDivision, setSavingDivision] = useState(false)
  const [deletingDivisionId, setDeletingDivisionId] = useState(null)

  const [savingGroupByDivision, setSavingGroupByDivision] = useState({})
  const [deletingGroupId, setDeletingGroupId] = useState(null)

  useEffect(() => {
    if (!tournamentId) return
    clubStore.tournament.getTournamentDivisions(tournamentId).catch(console.error)
  }, [tournamentId])

  const divisions = useMemo(() => {
    return (clubStore.tournament.divisions || []).slice().sort((a, b) => {
      const so = (a?.sort_order || 0) - (b?.sort_order || 0)
      if (so !== 0) return so
      return (a?.id || 0) - (b?.id || 0)
    })
  }, [clubStore.tournament.divisions])

  const resetDivisionForm = () => {
    setDivisionForm({...EMPTY_DIVISION_FORM})
    setShowDivisionForm(false)
  }

  const openAddDivision = () => {
    setDivisionForm({...EMPTY_DIVISION_FORM})
    setShowDivisionForm(true)
  }

  const openEditDivision = (division) => {
    setDivisionForm({
      id: division.id || 0,
      name: division.name || '',
      age_from: division.age_from ?? '',
      age_to: division.age_to ?? '',
      sex: division.sex || '',
      sort_order: division.sort_order || 0,
    })
    setShowDivisionForm(true)
  }

  const saveDivision = async () => {
    if (!divisionForm.name?.trim()) {
      systemStore.setError(appStore.tr.name_required || 'Name is required')
      return
    }

    try {
      setSavingDivision(true)

      const ret = await clubStore.tournament.saveTournamentDivision({
        id: divisionForm.id || undefined,
        tournament_id: tournamentId,
        name: divisionForm.name?.trim(),
        age_from: numOrNull(divisionForm.age_from),
        age_to: numOrNull(divisionForm.age_to),
        sex: divisionForm.sex || null,
        sort_order: toInt(divisionForm.sort_order, 0),
      })

      if (ret?.result === 0) {
        systemStore.setSuccess(appStore.tr.successfully_saved || 'Successfully saved')
        resetDivisionForm()
      }
    } catch (err) {
      systemStore.setError(err?.message || 'Save failed')
    } finally {
      setSavingDivision(false)
    }
  }

  const removeDivision = async (division) => {
    if (!window.confirm(appStore.tr.are_you_sure_delete || 'Are you sure?')) return

    try {
      setDeletingDivisionId(division.id)
      const ret = await clubStore.tournament.deleteTournamentDivision(division.id, tournamentId)
      if (ret?.result === 0) {
        systemStore.setSuccess(appStore.tr.successfully_deleted || 'Successfully deleted')
      }
    } catch (err) {
      systemStore.setError(err?.message || 'Delete failed')
    } finally {
      setDeletingDivisionId(null)
    }
  }

  const getGroupForm = (divisionId) => {
    return groupFormByDivision[divisionId] || {
      ...EMPTY_GROUP_FORM,
      division_id: divisionId,
    }
  }

  const setGroupForm = (divisionId, patch) => {
    setGroupFormByDivision((prev) => ({
      ...prev,
      [divisionId]: {
        ...getGroupForm(divisionId),
        ...patch,
      },
    }))
  }

  const openAddGroup = (divisionId) => {
    setGroupFormByDivision((prev) => ({
      ...prev,
      [divisionId]: {
        ...EMPTY_GROUP_FORM,
        division_id: divisionId,
      },
    }))
    setShowGroupFormByDivision((prev) => ({
      ...prev,
      [divisionId]: true,
    }))
  }

  const openEditGroup = (divisionId, group) => {
    setGroupFormByDivision((prev) => ({
      ...prev,
      [divisionId]: {
        id: group.id || 0,
        division_id: divisionId,
        name: group.name || '',
        sort_order: group.sort_order || 0,
      },
    }))
    setShowGroupFormByDivision((prev) => ({
      ...prev,
      [divisionId]: true,
    }))
  }

  const closeGroupForm = (divisionId) => {
    setShowGroupFormByDivision((prev) => ({
      ...prev,
      [divisionId]: false,
    }))
    setGroupFormByDivision((prev) => ({
      ...prev,
      [divisionId]: {
        ...EMPTY_GROUP_FORM,
        division_id: divisionId,
      },
    }))
  }

  const saveGroup = async (divisionId) => {
    const form = getGroupForm(divisionId)

    if (!form.name?.trim()) {
      systemStore.setError(appStore.tr.name_required || 'Name is required')
      return
    }

    try {
      setSavingGroupByDivision((prev) => ({
        ...prev,
        [divisionId]: true,
      }))

      const ret = await clubStore.tournament.saveTournamentGroup({
        id: form.id || undefined,
        tournament_id: tournamentId,
        division_id: divisionId,
        name: form.name?.trim(),
        sort_order: toInt(form.sort_order, 0),
      })

      if (ret?.result === 0) {
        systemStore.setSuccess(appStore.tr.successfully_saved || 'Successfully saved')
        closeGroupForm(divisionId)
      }
    } catch (err) {
      systemStore.setError(err?.message || 'Save failed')
    } finally {
      setSavingGroupByDivision((prev) => ({
        ...prev,
        [divisionId]: false,
      }))
    }
  }

  const removeGroup = async (divisionId, groupId) => {
    if (!window.confirm(appStore.tr.are_you_sure_delete || 'Are you sure?')) return

    try {
      setDeletingGroupId(groupId)
      const ret = await clubStore.tournament.deleteTournamentGroup(groupId, divisionId, tournamentId)
      if (ret?.result === 0) {
        systemStore.setSuccess(appStore.tr.successfully_deleted || 'Successfully deleted')
      }
    } catch (err) {
      systemStore.setError(err?.message || 'Delete failed')
    } finally {
      setDeletingGroupId(null)
    }
  }

  const sexText = (sex) => {
    if (sex === 'M') return appStore.tr.male;
    if (sex === 'F') return appStore.tr.female;
    return appStore.tr.mixed;
  }

  const renderDivisionHeader = (division) => {
    const groupsCount = division?.groups?.length || 0
    const ageText =
      division.age_from != null || division.age_to != null
        ? `${division.age_from ?? '-'} - ${division.age_to ?? '-'}`
        : null

    return (
      <div className="competition-divisions__accordion-heading">
        <span className="competition-divisions__accordion-icon"><i className="pi pi-sitemap" aria-hidden="true" /></span>
        <div className="competition-divisions__accordion-main">
          <span className="competition-divisions__accordion-title">{division.name}</span>
          <span className="competition-divisions__accordion-description">
            {[
              ageText ? `${appStore.tr.age}: ${ageText}` : null,
              division.sex ? `${appStore.tr.sex}: ${sexText(division.sex)}` : null,
            ].filter(Boolean).join(' · ')}
          </span>
        </div>
        <span className="competition-divisions__counter">
          <i className="pi pi-th-large" aria-hidden="true" /> {appStore.tr.groups}: {groupsCount}
        </span>
      </div>
    )
  }

  const renderDivisionForm = () => {
    if (!showDivisionForm) return null

    return (
      <div className="competition-divisions__editor">
        <div className="competition-divisions__editor-heading">
          <span className="competition-divisions__editor-icon"><i className="pi pi-pencil" aria-hidden="true" /></span>
          <span className="competition-divisions__editor-title">
            {divisionForm.id ? (appStore.tr.edit || 'Edit') : (appStore.tr.add_division || 'Add Division')}
          </span>
        </div>
        <div className="competition-divisions__form-grid">
          <div className="competition-divisions__field">
            <label htmlFor="division_name">{appStore.tr.name || 'Name'}</label>
            <Input id="division_name" value={divisionForm.name}
                   onChange={(e) => setDivisionForm((prev) => ({...prev, name: e.target.value}))} />
          </div>
          <div className="competition-divisions__field">
            <label htmlFor="division_sex">{appStore.tr.sex || 'Sex'}</label>
            <Select options={SEX_OPTIONS} value={divisionForm.sex}
                    onChange={(v) => setDivisionForm((prev) => ({...prev, sex: v}))}
                    id="division_sex"
                    itemTemplate={(item) => <>{appStore.tr[item.name] || item.name}</>}
                    valueTemplate={(val) => <>{val.id ? appStore.tr[val.name] : appStore.please_select}</>} />
          </div>
          <div className="competition-divisions__field competition-divisions__slider-field">
            <label htmlFor="division_age_from">{appStore.tr.age_from}
              <span className="competition-divisions__slider-value">{divisionForm.age_from}</span>
            </label>
            <Slider value={divisionForm.age_from}
                    onChange={(e) => setDivisionForm((prev) => ({...prev, age_from: e.value}))}
                    min={3} max={100} id="division_age_from" />
          </div>
          <div className="competition-divisions__field competition-divisions__slider-field">
            <label htmlFor="division_age_to">{appStore.tr.age_to}
              <span className="competition-divisions__slider-value">{divisionForm.age_to}</span>
            </label>
            <Slider value={divisionForm.age_to}
                    onChange={(e) => setDivisionForm((prev) => ({...prev, age_to: e.value}))}
                    min={3} max={100} id="division_age_to" />
          </div>
          <div className="competition-divisions__field competition-divisions__slider-field">
            <label htmlFor="division_sort_order">{appStore.tr.sort_order}
              <span className="competition-divisions__slider-value">{divisionForm.sort_order}</span>
            </label>
            <Slider value={divisionForm.sort_order}
                    onChange={(e) => setDivisionForm((prev) => ({...prev, sort_order: e.value}))}
                    min={1} max={50} id="division_sort_order" />
          </div>
        </div>
        <div className="competition-divisions__form-actions">
          <Btn icon="pi pi-save" label={appStore.tr.save} severity="success"
               loading={savingDivision} onClick={saveDivision} />
          <Btn icon="pi pi-times" label={appStore.tr.cancel} severity="secondary"
               outlined onClick={resetDivisionForm} />
        </div>
      </div>
    )
  }

  const renderGroupForm = (divisionId) => {
    if (!showGroupFormByDivision[divisionId]) return null

    const form = getGroupForm(divisionId)
    const loading = !!savingGroupByDivision[divisionId]

    return (
      <div className="competition-divisions__group-editor">
        <div className="competition-divisions__editor-heading">
          <span className="competition-divisions__editor-icon"><i className="pi pi-pencil" aria-hidden="true" /></span>
          <span className="competition-divisions__editor-title">
            {form.id ? (appStore.tr.edit || 'Edit') : (appStore.tr.add_group || 'Add Group')}
          </span>
        </div>
        <div className="competition-divisions__group-form-grid">
          <div className="competition-divisions__field">
            <label htmlFor={`group_name_${divisionId}`}>{appStore.tr.name || 'Name'}</label>
            <Input id={`group_name_${divisionId}`} value={form.name}
                   onChange={(e) => setGroupForm(divisionId, {name: e.target.value})} />
          </div>
          <div className="competition-divisions__field competition-divisions__slider-field">
            <label htmlFor={`group_sort_${divisionId}`}>{appStore.tr.sort_order}
              <span className="competition-divisions__slider-value">{form.sort_order}</span>
            </label>
            <Slider value={form.sort_order}
                    onChange={(e) => setGroupForm(divisionId, {sort_order: e.value})}
                    min={1} max={20} id={`group_sort_${divisionId}`} />
          </div>
        </div>
        <div className="competition-divisions__form-actions">
          <Btn icon="pi pi-save" label={appStore.tr.save} severity="success"
               loading={loading} onClick={() => saveGroup(divisionId)} />
          <Btn icon="pi pi-times" label={appStore.tr.cancel} severity="secondary"
               outlined onClick={() => closeGroupForm(divisionId)} />
        </div>
      </div>
    )
  }

  const renderGroupRow = (divisionId, group) => {
    return (
      <div key={group.id} className="competition-divisions__group-card">
        <span className="competition-divisions__group-icon"><i className="pi pi-users" aria-hidden="true" /></span>
        <div className="competition-divisions__group-main">
          <div className="competition-divisions__group-name">{group.name}</div>
          <div className="competition-divisions__group-detail">
            {appStore.tr.sort_order || 'Sort Order'}: {group.sort_order || 0}
          </div>
        </div>
        <div className="competition-divisions__group-actions">
          <Btn icon="pi pi-pencil" tooltip={appStore.tr.edit || 'Edit'}
               outlined onClick={() => openEditGroup(divisionId, group)} />
          <Btn icon="pi pi-trash" severity="danger" outlined
               tooltip={appStore.tr.delete || 'Delete'}
               loading={deletingGroupId === group.id}
               onClick={() => removeGroup(divisionId, group.id)} />
        </div>
      </div>
    )
  }

  const renderDivisionContent = (division) => {
    const groups = (division.groups || []).slice().sort((a, b) => {
      const so = (a?.sort_order || 0) - (b?.sort_order || 0)
      if (so !== 0) return so
      return (a?.id || 0) - (b?.id || 0)
    })

    return (
      <div className="competition-divisions__content">
        <div className="competition-divisions__toolbar">
          <div className="competition-divisions__toolbar-title">
            <i className="pi pi-th-large" aria-hidden="true" />
            <span>{appStore.tr.groups}: {groups.length}</span>
          </div>
          <div className="competition-divisions__toolbar-actions">
            <Btn icon="pi pi-plus" label={appStore.tr.add_group} severity="success"
                 onClick={() => openAddGroup(division.id)} />
            <Btn icon="pi pi-pencil" label={appStore.tr.edit || 'Edit'} outlined
                 onClick={() => openEditDivision(division)} />
            <Btn icon="pi pi-trash" label={appStore.tr.delete || 'Delete'}
                 severity="danger" outlined loading={deletingDivisionId === division.id}
                 onClick={() => removeDivision(division)} />
          </div>
        </div>

        {renderGroupForm(division.id)}

        {!groups.length ? (
          <div className="competition-divisions__empty competition-divisions__empty--inner">
            <i className="pi pi-users" aria-hidden="true" />
            <span>{appStore.tr.no_group}</span>
          </div>
        ) : (
          <div className="competition-divisions__groups-grid">
            {groups.map((group) => renderGroupRow(division.id, group))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="competition-divisions">
      <div className="competition-divisions__heading">
        <div className="competition-divisions__heading-main">
          <span className="competition-divisions__heading-icon"><i className="pi pi-sitemap" aria-hidden="true" /></span>
          <h4>{appStore.tr.divisions || 'Divisions'}</h4>
          <span className="competition-divisions__count">{divisions.length}</span>
        </div>
        <Btn icon="pi pi-plus" label={appStore.tr.add_division || 'Add Division'}
             severity="success" onClick={openAddDivision} />
      </div>

      {renderDivisionForm()}

      {!divisions.length ? (
        <div className="competition-divisions__empty">
          <i className="pi pi-sitemap" aria-hidden="true" />
          <span>{appStore.tr.no_division}</span>
        </div>
      ) : (
        <Accordion className="competition-divisions__accordion"
                   activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
          {divisions.map((division) => (
            <AccordionTab key={division.id} header={renderDivisionHeader(division)}>
              {renderDivisionContent(division)}
            </AccordionTab>
          ))}
        </Accordion>
      )}
    </div>
  )
}

export default observer(CompetitionDivisions)
